import React from 'react';
import { Link } from 'react-router-dom';
import { FaTrash, FaPlus, FaMinus, FaTimes, FaEye } from 'react-icons/fa';
import { useCart } from '../../contexts/CartContext';

export default function CartModal({ isOpen, onClose }) {
  const {
    items,
    updateQuantity,
    removeFromCart,
    clearCart,
    getTotalPrice,
    getTotalSavings,
    getTotalItems,
  } = useCart();

  if (!isOpen) return null;

  const handleIncreaseQuantity = (id, currentQuantity) => {
    updateQuantity(id, currentQuantity + 1);
  };

  const handleDecreaseQuantity = (id, currentQuantity) => {
    if (currentQuantity > 1) {
      updateQuantity(id, currentQuantity - 1);
    } else {
      removeFromCart(id);
    }
  };

  const totalPrice = getTotalPrice();
  const totalSavings = getTotalSavings();
  const totalItems = getTotalItems();

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50 animate-fade-in" onClick={onClose} />

      {/* Modal */}
      <div className="relative h-screen w-full max-w-md bg-df-surface shadow-2xl animate-slide-in overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-df-border">
            <h2 className="text-xl font-bold text-df-text">
              Carrito de Compras
              {totalItems > 0 && (
                <span className="ml-2 bg-df-accent/15 text-df-accent-hover text-sm px-2 py-1 rounded-full">
                  {totalItems} item{totalItems !== 1 ? 's' : ''}
                </span>
              )}
            </h2>
            <button onClick={onClose} className="text-df-muted hover:text-df-text p-1">
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Cart Content */}
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-df-muted">
                <div className="text-6xl mb-4">🛒</div>
                <p className="text-lg font-medium">Tu carrito está vacío</p>
                <p className="text-sm">¡Agrega algunos productos increíbles!</p>
              </div>
            </div>
          ) : (
            <>
              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {items.map(item => (
                  <div key={item.id} className="bg-df-surface2 rounded-lg p-4">
                    <div className="flex gap-3">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-contain rounded"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm line-clamp-2 mb-1">{item.name}</h3>
                        {item.brand && <p className="text-xs text-df-muted mb-2">{item.brand}</p>}

                        {/* Price */}
                        <div className="mb-2">
                          {item.isOnSale ? (
                            <div>
                              <span className="text-xs text-df-muted line-through">
                                ${item.originalPrice.toLocaleString()}
                              </span>
                              <span className="text-sm font-bold text-df-gold ml-2">
                                ${item.price.toLocaleString()}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm font-bold text-df-gold">
                              ${item.price.toLocaleString()}
                            </span>
                          )}
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleDecreaseQuantity(item.id, item.quantity)}
                              className="bg-df-surface3 hover:bg-df-border text-df-text w-6 h-6 rounded flex items-center justify-center text-xs"
                            >
                              <FaMinus />
                            </button>
                            <span className="bg-df-surface px-2 py-1 rounded border border-df-border text-df-text text-sm font-medium min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleIncreaseQuantity(item.id, item.quantity)}
                              className="bg-df-accent-strong hover:bg-df-accent-deep text-white w-6 h-6 rounded flex items-center justify-center text-xs"
                            >
                              <FaPlus />
                            </button>
                          </div>

                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-df-danger hover:opacity-80 p-1"
                            title="Eliminar del carrito"
                          >
                            <FaTrash className="text-sm" />
                          </button>
                        </div>

                        {/* Subtotal */}
                        <div className="mt-3 text-right bg-df-surface2 p-2 rounded">
                          <span className="text-xs text-df-muted">Subtotal:</span>
                          <div className="text-sm font-bold text-df-gold">
                            ${(item.price * item.quantity).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="border-t border-df-border bg-df-surface p-4 space-y-4">
                {/* Summary */}
                <div className="space-y-2">
                  {totalSavings > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-df-success">Ahorros totales:</span>
                      <span className="text-df-success font-semibold">
                        -${totalSavings.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-df-gold">${totalPrice.toLocaleString()}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <Link
                    to="/checkout"
                    onClick={e => {
                      // Cerrar el modal antes de navegar
                      onClose();
                      // Asegurarse de que la navegación no se vea afectada
                      e.stopPropagation();
                    }}
                    className="block w-full bg-df-accent-strong hover:bg-df-accent-deep text-white py-3 rounded-lg font-semibold text-center transition-colors"
                  >
                    Proceder al Pago
                  </Link>
                  <Link
                    to="/carrito"
                    onClick={onClose}
                    className="w-full bg-df-surface2 hover:bg-df-surface3 text-df-text py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <FaEye />
                    Ver Carrito Completo
                  </Link>
                  <button
                    onClick={clearCart}
                    className="w-full bg-df-danger/10 hover:bg-df-danger/20 text-df-danger py-2 rounded-lg font-medium transition-colors"
                  >
                    Vaciar Carrito
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
